import React, { useState } from 'react'
import {
  StyledGiftBasketBadge,
  StyledGiftBasketButton,
  StyledGiftBasketIcon,
  StyledGiftBasketLoadingIndicator,
  StyledGiftBasketSpinner,
  StyledGiftImage,
  StyledGiftImageFallback,
  StyledGiftImageWrapper,
  StyledGiftPlaceholder,
  StyledGiftPlusBadge,
  StyledGiftPlusIcon,
  StyledGiftTickIcon,
  StyledGiftPrice,
  StyledGiftTextSection,
  StyledGiftTitle,
} from '../ConciergeGiftTile/styled'
import { ProductMessageItem } from '../../ChatMessages'

type ConciergeGiftTileContentProps = {
  imageTestId?: string
  item: ProductMessageItem
  onBasketClick?: (item: ProductMessageItem) => void
  showBasketBadge?: boolean
  isBasketLoading?: boolean
  isBasketConfirmed?: boolean
}

type BasketButtonContentProps = {
  isBasketLoading: boolean
  isBasketConfirmed: boolean
}

const BasketButtonContent = ({
  isBasketLoading,
  isBasketConfirmed,
}: BasketButtonContentProps) => {
  if (isBasketLoading) {
    return (
      <StyledGiftBasketSpinner aria-hidden="true">
        <StyledGiftBasketLoadingIndicator />
      </StyledGiftBasketSpinner>
    )
  }

  if (isBasketConfirmed) {
    return <StyledGiftTickIcon aria-hidden="true" />
  }

  return (
    <>
      <StyledGiftBasketIcon />
      <StyledGiftPlusBadge aria-hidden="true">
        <StyledGiftPlusIcon />
      </StyledGiftPlusBadge>
    </>
  )
}

export const ConciergeGiftTileContent = ({
  imageTestId,
  item,
  onBasketClick,
  showBasketBadge = true,
  isBasketLoading = false,
  isBasketConfirmed = false,
}: ConciergeGiftTileContentProps) => {
  const [hasImageError, setHasImageError] = useState(false)

  return (
    <StyledGiftPlaceholder>
      {!item.imageUrl || hasImageError ? (
        <StyledGiftImageFallback data-testid={imageTestId}>
          Failed to load image
        </StyledGiftImageFallback>
      ) : (
        <StyledGiftImageWrapper>
          <StyledGiftImage
            alt={item.title}
            data-testid={imageTestId}
            draggable={false}
            src={item.imageUrl}
            onError={() => setHasImageError(true)}
          />
          {onBasketClick && (
            <StyledGiftBasketButton
              aria-label={`Add ${item.title} to basket`}
              aria-busy={isBasketLoading}
              disabled={isBasketLoading}
              type="button"
              onClick={() => onBasketClick(item)}
            >
              <BasketButtonContent
                isBasketLoading={isBasketLoading}
                isBasketConfirmed={isBasketConfirmed}
              />
            </StyledGiftBasketButton>
          )}
          {!onBasketClick && showBasketBadge && (
            <StyledGiftBasketBadge
              aria-hidden="true"
              data-testid="chat-gift-basket-badge"
            >
              <StyledGiftBasketIcon />
              <StyledGiftPlusBadge>
                <StyledGiftPlusIcon />
              </StyledGiftPlusBadge>
            </StyledGiftBasketBadge>
          )}
        </StyledGiftImageWrapper>
      )}
      <StyledGiftTextSection>
        <StyledGiftTitle>{item.title}</StyledGiftTitle>
        {item.price && <StyledGiftPrice>{item.price}</StyledGiftPrice>}
      </StyledGiftTextSection>
    </StyledGiftPlaceholder>
  )
}
